import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  Boxes,
  CalendarClock,
  ClipboardList,
  IndianRupee,
  PackageX,
  Recycle,
  RefreshCcw,
  ShoppingCart,
  Utensils,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Badge } from "@/components/ui/badge";
import {
  AnalyticsExportButtons,
  AnalyticsFilters,
  ChartFrame,
  DataTable,
  KpiCard,
  MetricBars,
  Panel,
  PIE_TONES,
  TONE_VAR,
  compactInr,
  inr,
  num,
  pct0,
  tooltipStyle,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { resolveSuiteWindow, type SuiteRangeKey } from "@/lib/admin/analytics-range";
import { getInventoryDashboard } from "@/lib/admin/inventory.functions";
import { getConsumptionReports } from "@/lib/admin/recipes.functions";
import { istToday } from "@/lib/admin/production.schemas";
import {
  MOVEMENT_REASON_LABEL,
  PO_STATUS_LABEL,
  STOCK_STATE_LABEL,
  type PurchaseOrder,
  type StockItem,
  type StockMovement,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/inventory-dashboard")({
  head: () => ({
    meta: [
      { title: "Inventory Dashboard — Admin" },
      {
        name: "description",
        content:
          "Live inventory value, consumption, purchases, wastage, low stock and supplier performance for the kitchen store.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InventoryDashboardPage,
});

function InventoryDashboardPage() {
  const [range, setRange] = useState<SuiteRangeKey>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const win = useMemo(() => resolveSuiteWindow({ range, from, to }), [range, from, to]);
  const fetchDashboard = useServerFn(getInventoryDashboard);

  const q = useQuery({
    queryKey: ["admin", "inventory", "dashboard", win.from, win.to],
    queryFn: () =>
      fetchDashboard({ data: { from: win.from, to: win.to, label: win.label } as never }),
    staleTime: 20_000,
  });

  // Recipe-driven consumption for the current service day.
  const today = istToday();
  const fetchConsumption = useServerFn(getConsumptionReports);
  const todayConsumption = useQuery({
    queryKey: ["admin", "inventory", "consumption-today", today],
    queryFn: () => fetchConsumption({ data: { from: today, to: today } }),
    staleTime: 20_000,
  });

  const data = q.data;
  const loading = q.isPending;
  const kpi = data?.kpi;


  const exportRows = useMemo(() =>
    (data?.low_stock ?? []).map((i) => ({
      SKU: i.sku,
      Ingredient: i.item_name,
      Category: i.category_name,
      Unit: i.unit,
      "Current stock": i.current_stock,
      "Reorder level": i.reorder_level,
      "Stock value": i.stock_value_paise / 100,
      State: STOCK_STATE_LABEL[i.stock_state],
    })), [data?.low_stock]);

  const { onExportCsv, onExportExcel } = AnalyticsExportButtons({
    name: "inventory-low-stock",
    rows: exportRows,
  });

  const lowColumns: Array<DataColumn<StockItem>> = [
    {
      key: "item",
      header: "Ingredient",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.item_name}</p>
          <p className="text-[11px] text-muted-foreground">
            {r.sku} · {r.category_name}
          </p>
        </div>
      ),
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "stock",
      header: "Stock",
      align: "right",
      render: (r) => `${r.current_stock} ${r.unit}`,
      sortValue: (r) => r.current_stock,
      exportValue: (r) => r.current_stock,
    },
    {
      key: "reorder",
      header: "Reorder at",
      align: "right",
      render: (r) => `${r.reorder_level} ${r.unit}`,
      sortValue: (r) => r.reorder_level,
      exportValue: (r) => r.reorder_level,
    },
    {
      key: "value",
      header: "Value",
      align: "right",
      render: (r) => inr(r.stock_value_paise / 100),
      sortValue: (r) => r.stock_value_paise,
      exportValue: (r) => r.stock_value_paise / 100,
    },
    {
      key: "state",
      header: "State",
      render: (r) => (
        <Badge variant={r.stock_state === "out" ? "destructive" : "secondary"}>
          {STOCK_STATE_LABEL[r.stock_state]}
        </Badge>
      ),
      exportValue: (r) => STOCK_STATE_LABEL[r.stock_state],
    },
  ];

  const expiringColumns: Array<DataColumn<StockItem>> = [
    {
      key: "item",
      header: "Ingredient",
      render: (r) => <span className="font-medium text-foreground">{r.item_name}</span>,
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "expiry",
      header: "Expiry",
      render: (r) => r.expiry_date ?? "—",
      sortValue: (r) => r.expiry_date ?? "",
      exportValue: (r) => r.expiry_date ?? "",
    },
    {
      key: "days",
      header: "Days left",
      align: "right",
      render: (r) => (
        <span className={r.days_to_expiry !== null && r.days_to_expiry <= 3 ? "text-destructive" : ""}>
          {r.days_to_expiry ?? "—"}
        </span>
      ),
      sortValue: (r) => r.days_to_expiry ?? 999,
      exportValue: (r) => r.days_to_expiry ?? "",
    },
    {
      key: "stock",
      header: "Stock",
      align: "right",
      render: (r) => `${r.current_stock} ${r.unit}`,
      sortValue: (r) => r.current_stock,
      exportValue: (r) => r.current_stock,
    },
    {
      key: "value",
      header: "Value at risk (₹)",
      align: "right",
      render: (r) => inr(r.stock_value_paise / 100),
      sortValue: (r) => r.stock_value_paise,
      exportValue: (r) => r.stock_value_paise / 100,
    },
  ];

  const poColumns: Array<DataColumn<PurchaseOrder>> = [
    {
      key: "po",
      header: "PO number",
      render: (r) => <span className="font-mono text-xs">{r.po_number}</span>,
      sortValue: (r) => r.po_number,
      exportValue: (r) => r.po_number,
    },
    {
      key: "supplier",
      header: "Supplier",
      render: (r) => r.supplier_name,
      sortValue: (r) => r.supplier_name,
      exportValue: (r) => r.supplier_name,
    },
    {
      key: "date",
      header: "Order date",
      render: (r) => r.order_date,
      sortValue: (r) => r.order_date,
      exportValue: (r) => r.order_date,
    },
    {
      key: "items",
      header: "Items",
      align: "right",
      render: (r) => num(r.lines.length),
      sortValue: (r) => r.lines.length,
      exportValue: (r) => r.lines.length,
    },
    {
      key: "total",
      header: "Total (₹)",
      align: "right",
      render: (r) => inr(r.total_paise / 100),
      sortValue: (r) => r.total_paise,
      exportValue: (r) => r.total_paise / 100,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge variant="secondary">{PO_STATUS_LABEL[r.status] ?? r.status}</Badge>,
      exportValue: (r) => PO_STATUS_LABEL[r.status] ?? r.status,
    },
  ];

  const adjustmentColumns: Array<DataColumn<StockMovement>> = [
    {
      key: "date",
      header: "Date",
      render: (r) => r.movement_date,
      sortValue: (r) => r.movement_date,
      exportValue: (r) => r.movement_date,
    },
    {
      key: "item",
      header: "Ingredient",
      render: (r) => r.item_name,
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "reason",
      header: "Reason",
      render: (r) => (
        <Badge variant="secondary">{MOVEMENT_REASON_LABEL[r.reason] ?? r.reason}</Badge>
      ),
      exportValue: (r) => MOVEMENT_REASON_LABEL[r.reason] ?? r.reason,
    },
    {
      key: "qty",
      header: "Movement",
      align: "right",
      render: (r) => (
        <span className={r.quantity < 0 ? "text-destructive" : "text-foreground"}>
          {r.quantity > 0 ? "+" : ""}
          {r.quantity} {r.unit}
        </span>
      ),
      sortValue: (r) => r.quantity,
      exportValue: (r) => r.quantity,
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (r) => `${r.balance_stock} ${r.unit}`,
      sortValue: (r) => r.balance_stock,
      exportValue: (r) => r.balance_stock,
    },
    {
      key: "by",
      header: "User",
      render: (r) => r.performed_by,
      exportValue: (r) => r.performed_by,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Inventory Dashboard"
        description="Stock value, consumption, purchasing, wastage and supplier performance for the kitchen store."
      />
      <RefreshBar
        targets={[{ key: "inventory", label: "inventory" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt}
      />
      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        status={loading ? "Loading…" : `${win.label} · ${win.from} → ${win.to}`}
        onExportCsv={onExportCsv}
        onExportExcel={onExportExcel}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label="Total inventory value"
          value={compactInr((kpi?.inventory_value_paise ?? 0) / 100)}
          tone="success"
          icon={Boxes}
          loading={loading}
          hint="Valued at average cost"
        />
        <KpiCard
          label="Current inventory cost"
          value={compactInr((kpi?.inventory_cost_paise ?? 0) / 100)}
          tone="info"
          icon={IndianRupee}
          loading={loading}
          hint="Valued at purchase price"
        />
        <KpiCard
          label="Today's consumption"
          value={compactInr((kpi?.consumption_today_paise ?? 0) / 100)}
          tone="warn"
          icon={Utensils}
          loading={loading}
          hint="Issued to kitchen"
        />
        <KpiCard
          label="Today's purchases"
          value={compactInr((kpi?.purchases_today_paise ?? 0) / 100)}
          tone="teal"
          icon={ShoppingCart}
          loading={loading}
          hint="Purchase orders raised"
        />
        <KpiCard
          label="Low stock items"
          value={num(kpi?.low_stock_items ?? 0)}
          tone="warn"
          icon={AlertTriangle}
          loading={loading}
          to="/admin/stock-items"
          hint="At or below reorder level"
        />
        <KpiCard
          label="Out of stock"
          value={num(kpi?.out_of_stock_items ?? 0)}
          tone="danger"
          icon={PackageX}
          loading={loading}
          to="/admin/stock-items"
          hint="Needs immediate purchase"
        />
        <KpiCard
          label="Expiring soon"
          value={num(kpi?.expiring_soon ?? 0)}
          tone="purple"
          icon={CalendarClock}
          loading={loading}
          hint="Within 14 days"
        />
        <KpiCard
          label="Waste today"
          value={compactInr((kpi?.waste_today_paise ?? 0) / 100)}
          tone="danger"
          icon={Recycle}
          loading={loading}
          hint="Waste, damage and expiry"
        />
        <KpiCard
          label="Pending purchase orders"
          value={num(kpi?.pending_pos ?? 0)}
          tone="info"
          icon={ClipboardList}
          loading={loading}
          to="/admin/purchase-orders"
          hint="Draft and sent"
        />
        <KpiCard
          label="Inventory turnover"
          value={`${(kpi?.turnover ?? 0).toFixed(2)}x`}
          tone="neutral"
          icon={RefreshCcw}
          loading={loading}
          hint={`Consumption ÷ stock · ${win.label}`}
        />
      </div>

      {/* Recipe consumption booked automatically when the kitchen marks orders ready. */}
      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <div className="grid grid-cols-2 gap-3 xl:col-span-1 xl:grid-cols-1">
          <KpiCard
            label="Today's consumption"
            value={compactInr((todayConsumption.data?.totals.consumption_cost_paise ?? 0) / 100)}
            tone="warn"
            icon={Utensils}
            loading={todayConsumption.isPending}
            hint={`${num(todayConsumption.data?.totals.portions ?? 0)} portions · ${today}`}
          />
          <KpiCard
            label="Today's waste"
            value={compactInr((kpi?.waste_today_paise ?? 0) / 100)}
            tone="danger"
            icon={Recycle}
            loading={loading}
            hint="Waste, damage and expiry write-offs"
          />
        </div>
        <Panel
          title="Today's ingredient consumption"
          subtitle={`${num(todayConsumption.data?.totals.ingredients_used ?? 0)} ingredients`}
          className="xl:col-span-2"
        >
          <MetricBars
            rows={(todayConsumption.data?.by_ingredient ?? []).slice(0, 8).map((r) => ({
              name: r.inventory_item_name,
              value: Math.round(r.cost_paise / 100),
            }))}
            tone="warn"
            loading={todayConsumption.isPending}
            money
          />
        </Panel>
      </div>



      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel title="Inventory value trend" subtitle={win.label} className="xl:col-span-2">
          <ChartFrame loading={loading} empty={(data?.value_trend.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.value_trend ?? []}>
                <defs>
                  <linearGradient id="invValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.success} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={TONE_VAR.success} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={70} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Area
                  type="monotone"
                  dataKey="value"
                  name="Stock value"
                  stroke={TONE_VAR.success}
                  fill="url(#invValue)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Category distribution" subtitle="Stock value">
          <ChartFrame loading={loading} empty={(data?.category_distribution.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data?.category_distribution ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="50%"
                  outerRadius="80%"
                  paddingAngle={2}
                >
                  {(data?.category_distribution ?? []).map((_, i) => (
                    <Cell key={i} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Purchase trend" subtitle="Value of orders raised">
          <ChartFrame loading={loading} empty={(data?.purchase_trend.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.purchase_trend ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Bar dataKey="value" name="Purchases" fill={TONE_VAR.teal} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Consumption trend" subtitle="Value issued to kitchen">
          <ChartFrame
            loading={loading}
            empty={(data?.consumption_trend.length ?? 0) === 0}
            height="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.consumption_trend ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Line
                  type="monotone"
                  dataKey="value"
                  name="Consumption"
                  stroke={TONE_VAR.warn}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Stock movement" subtitle="Inbound vs outbound value">
          <ChartFrame loading={loading} empty={(data?.movement_trend.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.movement_trend ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="inbound" name="Inbound" fill={TONE_VAR.success} radius={[4, 4, 0, 0]} />
                <Bar dataKey="outbound" name="Outbound" fill={TONE_VAR.danger} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Fast moving items" subtitle="Quantity consumed">
          <MetricBars rows={data?.fast_moving ?? []} tone="warn" loading={loading} />
        </Panel>
        <Panel title="Slow moving items" subtitle="No movement in window">
          <MetricBars rows={data?.slow_moving ?? []} tone="neutral" loading={loading} money />
        </Panel>
        <Panel title="Waste analysis" subtitle="Value by reason">
          <MetricBars rows={data?.waste_analysis ?? []} tone="danger" loading={loading} money />
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel title="Supplier performance" subtitle="Top suppliers by purchase value">
          <DataTable
            rows={data?.supplier_performance ?? []}
            loading={loading}
            exportName="supplier-performance"
            pageSize={8}
            minWidth={560}
            searchOn={(r) => r.name}
            columns={[
              {
                key: "name",
                header: "Supplier",
                render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => num(r.orders),
                sortValue: (r) => r.orders,
                exportValue: (r) => r.orders,
              },
              {
                key: "value",
                header: "Purchase value",
                align: "right",
                render: (r) => inr(r.value_paise / 100),
                sortValue: (r) => r.value_paise,
                exportValue: (r) => r.value_paise / 100,
              },
              {
                key: "otd",
                header: "On time",
                align: "right",
                render: (r) => pct0(r.on_time_rate),
                sortValue: (r) => r.on_time_rate,
                exportValue: (r) => r.on_time_rate,
              },
              {
                key: "rating",
                header: "Rating",
                align: "right",
                render: (r) => r.rating.toFixed(1),
                sortValue: (r) => r.rating,
                exportValue: (r) => r.rating,
              },
            ]}
          />
        </Panel>

        <Panel title="Most consumed ingredients" subtitle={win.label}>
          <DataTable
            rows={data?.most_consumed ?? []}
            loading={loading}
            exportName="most-consumed"
            pageSize={8}
            minWidth={520}
            searchOn={(r) => r.name}
            columns={[
              {
                key: "name",
                header: "Ingredient",
                render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "qty",
                header: "Quantity",
                align: "right",
                render: (r) => `${r.quantity} ${r.unit}`,
                sortValue: (r) => r.quantity,
                exportValue: (r) => r.quantity,
              },
              {
                key: "value",
                header: "Value",
                align: "right",
                render: (r) => inr(r.value_paise / 100),
                sortValue: (r) => r.value_paise,
                exportValue: (r) => r.value_paise / 100,
              },
            ]}
          />
        </Panel>

        <Panel title="Low stock" subtitle="Reorder required">
          <DataTable
            rows={data?.low_stock ?? []}
            columns={lowColumns}
            loading={loading}
            exportName="low-stock"
            pageSize={8}
            searchOn={(r) => `${r.item_name} ${r.sku} ${r.category_name}`}
          />
        </Panel>

        <Panel title="Expiring items" subtitle="Next 14 days">
          <DataTable
            rows={data?.expiring ?? []}
            columns={expiringColumns}
            loading={loading}
            exportName="expiring-items"
            pageSize={8}
            searchOn={(r) => r.item_name}
          />
        </Panel>

        <Panel title="Latest purchases" subtitle="Most recent purchase orders">
          <DataTable
            rows={data?.latest_purchases ?? []}
            columns={poColumns}
            loading={loading}
            exportName="latest-purchases"
            pageSize={8}
            searchOn={(r) => `${r.po_number} ${r.supplier_name}`}
          />
        </Panel>

        <Panel title="Recent stock adjustments" subtitle="Audit trail">
          <DataTable
            rows={data?.recent_adjustments ?? []}
            columns={adjustmentColumns}
            loading={loading}
            exportName="recent-adjustments"
            pageSize={8}
            searchOn={(r) => `${r.item_name} ${r.reason} ${r.performed_by}`}
          />
        </Panel>
      </div>
    </div>
  );
}
